// src/utils/locationUtils.js - FIXED VERSION

import { normalizeOrderType } from './orderTypeUtils';

/**
 * Determines if the current user is located in Israel
 * @param {Object} userData - Optional user data object, will read from localStorage if not provided
 * @returns {boolean} True if user is in Israel
 */
export const isIsraelUser = (userData = null) => {
  try {
    // If no userData passed, get it from localStorage
    const user = userData || JSON.parse(localStorage.getItem('user') || '{}');
    const location = user.UserLocation?.toUpperCase().trim();
        
    // Check for various Israel location formats
    return location === 'ISRAEL' || 
           location === 'IL' || 
           location?.includes('ISRAEL');
  } catch (error) {
    console.error('Error checking Israel user status:', error);
    return false;
  }
};
  
  /**
   * Determines if QuickBooks integration is required for the current user
   * @param {Object} userData - Optional user data object
   * @returns {boolean} True if QuickBooks is required (non-Israel users)
   */
  export const requiresQuickBooks = (userData = null) => {
    return !isIsraelUser(userData);
  };
  
  /**
   * Gets the appropriate invoice prefix based on user location
   * @param {Object} userData - Optional user data object
   * @returns {string} Invoice prefix ('IL-' for Israel, 'US-' for US users)
   */
  export const getInvoicePrefix = (userData = null) => {
    return isIsraelUser(userData) ? 'IL-' : 'US-';
  };
  
  /**
   * 🔧 FIXED: Determines if tax calculation is needed based on location and order type
   * @param {string} orderType - Type of order
   * @param {Object} userData - Optional user data object
   * @returns {boolean} True if tax calculation is needed
   */
  export const requiresTaxCalculation = (orderType, userData = null) => {
    // Israel users never need tax calculation
    if (isIsraelUser(userData)) {
      return false;
    }
    
    // 🔧 FIX: For non-Israel users, ONLY New Sale Wig orders need tax
    // New Wig orders are tax exempt (custom manufacturing)
    // "Aidel New Wig" follows the same rules as "New Wig".
    const baseType = normalizeOrderType(orderType);
    return baseType === 'New Wig' || baseType === 'New Sale Wig';
  };

  /**
   * 🔧 FIXED: Determines if an order type is tax exempt
   * @param {string} orderType - Type of order
   * @param {Object} userData - Optional user data object
   * @returns {boolean} True if order is tax exempt
   */
  export const isTaxExemptOrder = (orderType, userData = null) => {
    // Israel users - handled separately 
    if (isIsraelUser(userData)) {
      return false; // Let Israel logic handle this
    }
    
    // 🔧 FIX: Tax exempt order types for US users
    // ONLY "New Sale Wig" is taxable
    // All others are tax exempt: New Wig, Wig Repair, Consultation, Accessory, Wash & Set, Recut
    // "Aidel New Wig" follows the same rules as "New Wig".
    const baseType = normalizeOrderType(orderType);
    return baseType !== 'New Wig' && baseType !== 'New Sale Wig';
  };
  
  /**
   * Gets user-friendly location display name
   * @param {Object} userData - Optional user data object
   * @returns {string} Display name for location
   */
  export const getLocationDisplayName = (userData = null) => {
    const user = userData || JSON.parse(localStorage.getItem('user') || '{}');
    const location = user.UserLocation?.toUpperCase().trim();
    
    switch (location) {
      case 'NY':
      case 'NEW YORK':
      case 'POMONA':
        return 'New York/Pomona';
      case 'NJ':
      case 'NEW JERSEY':
      case 'LAKEWOOD':
        return 'New Jersey/Lakewood';
      case 'ISRAEL':
      case 'IL':
        return 'Israel';
      default:
        return location || 'Unknown Location';
    }
  };
  
  /**
   * Gets the tax rate for a given location
   * @param {string} location - Location string
   * @returns {Object} Tax rate info with rate, state, and source
   */
  export const getTaxRateForLocation = (location) => {
    if (!location) {
      return { taxRate: 0, state: null, source: 'no_location' };
    }
  
    const loc = location.toUpperCase().trim();
    
    switch (loc) {
      case 'NY':
      case 'NEW YORK':
      case 'POMONA':
        return { taxRate: 8.375, state: 'NY', source: 'location_ny' };
      case 'NJ':
      case 'NEW JERSEY':
      case 'LAKEWOOD':
        return { taxRate: 6.625, state: 'NJ', source: 'location_nj' };
      case 'ISRAEL':
      case 'IL':
        return { taxRate: 0, state: 'ISRAEL', source: 'israel_location' };
      default:
        return { taxRate: 0, state: null, source: 'unknown_location' };
    }
  };
  
  /**
   * 🔧 FIXED: Determines tax rate based on user location and order type
   * @param {string} orderType - Type of order
   * @param {Object} userData - Optional user data object
   * @returns {Object} Tax calculation result
   */
  export const calculateTaxForOrder = (orderType, userData = null) => {
    console.log('🔍 === CALCULATE TAX FOR ORDER ===');
    console.log('Order type:', orderType);
    
    // Israel users never pay tax
    if (isIsraelUser(userData)) {
      console.log('🇮🇱 Israel user - no tax calculation needed');
      return { taxRate: 0, state: 'ISRAEL', source: 'israel_user' };
    }
    
    // 🔧 FIX: Only apply tax if order type requires it for non-Israel users
    if (!requiresTaxCalculation(orderType, userData)) {
      console.log(`❌ Order type "${orderType}" is tax exempt`);
      return { taxRate: 0, state: null, source: 'tax_exempt_order' };
    }
  
    // For non-Israel users with taxable orders, use user location for tax
    console.log('📍 Tax determination: Using user location for taxable order');
    
    const user = userData || JSON.parse(localStorage.getItem('user') || '{}');
    console.log('📱 User data from localStorage:', user);
    
    if (user.UserLocation) {
      const location = user.UserLocation;
      console.log('🏢 User working from location:', location);
      
      const taxInfo = getTaxRateForLocation(location);
      console.log('→ Tax calculation result:', taxInfo);
      return taxInfo;
    }
  
    console.log('❌ No user location found, cannot determine tax');
    return { taxRate: 0, state: null, source: 'no_user_location' };
  };
  
  /**
   * Gets available tax locations for manual selection
   * @returns {Array} Array of location options
   */
  export const getAvailableTaxLocations = () => {
    return [
      { value: 'NY', label: 'New York / Pomona (8.375%)', taxRate: 8.375, state: 'NY' },
      { value: 'NJ', label: 'New Jersey / Lakewood (6.625%)', taxRate: 6.625, state: 'NJ' },
      { value: 'ISRAEL', label: 'Israel (0%)', taxRate: 0, state: 'ISRAEL' },
      { value: 'OTHER', label: 'Other Location (0%)', taxRate: 0, state: 'OTHER' }
    ];
  };
  
  /**
   * Validates if a location string is recognized
   * @param {string} location - Location to validate
   * @returns {boolean} True if location is recognized
   */
  export const isValidLocation = (location) => {
    if (!location) return false;
    
    const validLocations = ['NY', 'NEW YORK', 'POMONA', 'NJ', 'NEW JERSEY', 'LAKEWOOD', 'ISRAEL', 'IL'];
    return validLocations.includes(location.toUpperCase().trim());
  };
  
  /**
   * Gets the processing mode for the current user
   * @param {Object} userData - Optional user data object
   * @returns {string} Processing mode ('LOCAL' for Israel, 'QUICKBOOKS' for others)
   */
  export const getProcessingMode = (userData = null) => {
    return isIsraelUser(userData) ? 'LOCAL' : 'QUICKBOOKS';
  };
  
  /**
   * Gets currency information for the user's location
   * @param {Object} userData - Optional user data object
   * @returns {Object} Currency information
   */
  export const getCurrencyInfo = (userData = null) => {
    const user = userData || JSON.parse(localStorage.getItem('user') || '{}');
    const location = user.UserLocation?.toUpperCase().trim();
    
    if (location === 'ISRAEL' || location === 'IL') {
      return {
        primary: 'USD', // Still using USD for now
        secondary: 'ILS', // Could implement ILS conversion later
        symbol: '$',
        needsConversion: false // Set to true when ILS support is added
      };
    }
    
    return {
      primary: 'USD',
      secondary: null,
      symbol: '$',
      needsConversion: false
    };
  };
  
  /**
   * Gets warehouse information based on location
   * @param {Object} userData - Optional user data object
   * @returns {Object} Warehouse information
   */
  export const getWarehouseInfo = (userData = null) => {
    const user = userData || JSON.parse(localStorage.getItem('user') || '{}');
    const location = user.UserLocation?.toUpperCase().trim();
    
    switch (location) {
      case 'NY':
      case 'NEW YORK':
      case 'POMONA':
        return { warehouse: 'Pomona', region: 'NY', timezone: 'America/New_York' };
      case 'NJ':
      case 'NEW JERSEY':
      case 'LAKEWOOD':
        return { warehouse: 'Lakewood', region: 'NJ', timezone: 'America/New_York' };
      case 'ISRAEL':
      case 'IL':
        return { warehouse: 'Israel', region: 'ISRAEL', timezone: 'Asia/Jerusalem' };
      default:
        return { warehouse: 'Pomona', region: 'DEFAULT', timezone: 'America/New_York' };
    }
  };
  
  /**
   * Gets business hours for the user's location
   * @param {Object} userData - Optional user data object
   * @returns {Object} Business hours information
   */
  export const getBusinessHours = (userData = null) => {
    const warehouseInfo = getWarehouseInfo(userData);
    
    if (warehouseInfo.region === 'ISRAEL') {
      return {
        timezone: 'Asia/Jerusalem',
        days: 'Sunday-Thursday', // Israel business days
        hours: '8:00 AM - 6:00 PM',
        closedOn: ['Friday', 'Saturday'], // Shabbat
        specialNotes: 'Closed for Jewish holidays'
      };
    }
    
    return {
      timezone: 'America/New_York',
      days: 'Monday-Friday',
      hours: '9:00 AM - 5:00 PM',
      closedOn: ['Saturday', 'Sunday'],
      specialNotes: 'Closed for US federal holidays'
    };
  };
  
  /**
   * Debug function to log location detection results
   * @param {Object} userData - Optional user data object
   */
  export const debugLocationDetection = (userData = null) => {
    const user = userData || JSON.parse(localStorage.getItem('user') || '{}');
    
    console.log('=== LOCATION DETECTION DEBUG ===');
    console.log('Raw user data:', user);
    console.log('UserLocation field:', user.UserLocation);
    console.log('Is Israel user:', isIsraelUser(userData));
    console.log('Requires QuickBooks:', requiresQuickBooks(userData));
    console.log('Location display name:', getLocationDisplayName(userData));
    console.log('Processing mode:', getProcessingMode(userData));
    console.log('Warehouse info:', getWarehouseInfo(userData));
    console.log('Currency info:', getCurrencyInfo(userData));
    console.log('Business hours:', getBusinessHours(userData));
    console.log('================================');
  };
  
  /**
   * Gets appropriate greeting based on user's location and time
   * @param {Object} userData - Optional user data object
   * @returns {string} Localized greeting
   */
  export const getLocalizedGreeting = (userData = null) => {
    const warehouseInfo = getWarehouseInfo(userData);
    const isIsrael = isIsraelUser(userData);
    
    // Get current time in user's timezone
    const now = new Date();
    const hour = now.getHours();
    
    let timeGreeting;
    if (hour < 12) {
      timeGreeting = 'Good morning';
    } else if (hour < 17) {
      timeGreeting = 'Good afternoon';
    } else {
      timeGreeting = 'Good evening';
    }
    
    if (isIsrael) {
      return `${timeGreeting} from Israel! 🇮🇱`;
    }
    
    return `${timeGreeting} from ${warehouseInfo.warehouse}!`;
  };

  /**
   * 🔧 NEW: Debug function to test tax logic for different order types
   * @param {Object} userData - Optional user data object
   */
  export const debugTaxLogic = (userData = null) => {
    const orderTypes = ['New Wig', 'New Sale Wig', 'Wig Repair', 'Consultation', 'Accessory'];
    
    console.log('=== TAX LOGIC DEBUG ===');
    console.log('User is Israel:', isIsraelUser(userData));
    
    orderTypes.forEach(orderType => {
      const requiresTax = requiresTaxCalculation(orderType, userData);
      const isTaxExempt = isTaxExemptOrder(orderType, userData);
      
      console.log(`${orderType}:`, {
        requiresTax,
        isTaxExempt,
        shouldBeTaxed: !isTaxExempt && requiresTax
      });
    });
    console.log('========================');
  };
  
  /**
   * Export all utilities as a default object as well
   */
  export default {
    isIsraelUser,
    requiresQuickBooks,
    getInvoicePrefix,
    requiresTaxCalculation,
    getLocationDisplayName,
    getTaxRateForLocation,
    calculateTaxForOrder,
    getAvailableTaxLocations,
    isValidLocation,
    getProcessingMode,
    getCurrencyInfo,
    getWarehouseInfo,
    getBusinessHours,
    debugLocationDetection,
    getLocalizedGreeting,
    isTaxExemptOrder,
    debugTaxLogic
  };